"use client";

import { useState, useEffect } from "react";
import { type PlanConfig, PLANS as FALLBACK_PLANS } from "@/lib/stripe";

// Module-level cache shared across all hook instances
let _clientCache: Record<string, PlanConfig> | null = null;
let _clientCacheExpires = 0;
let _fetchPromise: Promise<Record<string, PlanConfig>> | null = null;
const CLIENT_CACHE_TTL = 60_000; // 1 minute

function dbRowToPlanConfig(row: any): PlanConfig {
  return {
    id: row.id,
    name: row.name,
    price: row.price_label || (row.price_monthly > 0 ? `$${row.price_monthly}/mo` : "$0"),
    priceMonthly: row.price_monthly != null ? Number(row.price_monthly) : null,
    description: row.description || "",
    videos: row.videos_per_month ?? Infinity,
    images: row.images_per_month ?? Infinity,
    brands: row.brands_limit ?? Infinity,
    users: row.users_limit ?? Infinity,
    exportsPerMonth: row.exports_per_month ?? Infinity,
    socialAccounts: row.social_accounts ?? Infinity,
    generationsPerDay: row.generations_per_day ?? Infinity,
    postsPerDay: row.posts_per_day ?? Infinity,
    repostsPerDay: row.reposts_per_day ?? Infinity,
    trialDays: row.trial_days ?? 0,
    canPublish: row.can_publish ?? false,
    editorDesignsPerMonth: row.editor_designs_per_month ?? Infinity,
    socialAuditFrequency: row.social_audit_frequency || null,
    visible: row.visible ?? true,
    sortOrder: row.sort_order ?? 0,
    stripePriceId: null, // never exposed client-side
  };
}

async function fetchPlans(): Promise<Record<string, PlanConfig>> {
  const now = Date.now();
  if (_clientCache && now < _clientCacheExpires) return _clientCache;

  // Deduplicate concurrent fetches
  if (_fetchPromise) return _fetchPromise;

  _fetchPromise = (async () => {
    try {
      const res = await fetch("/api/plans");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { plans } = await res.json();

      if (!plans || !Array.isArray(plans) || plans.length === 0) {
        throw new Error("Empty plans response");
      }

      const mapped: Record<string, PlanConfig> = {};
      for (const row of plans) {
        mapped[row.id] = dbRowToPlanConfig(row);
      }
      // Add trial alias
      if (mapped.free && !mapped.trial) {
        mapped.trial = mapped.free;
      }

      _clientCache = mapped;
      _clientCacheExpires = Date.now() + CLIENT_CACHE_TTL;
      return mapped;
    } catch (err) {
      console.warn("[usePlans] Fetch failed, using fallback:", err);
      return FALLBACK_PLANS;
    } finally {
      _fetchPromise = null;
    }
  })();

  return _fetchPromise;
}

export function usePlans() {
  const [plans, setPlans] = useState<Record<string, PlanConfig>>(
    _clientCache || FALLBACK_PLANS
  );
  const [isLoading, setIsLoading] = useState(!_clientCache);

  useEffect(() => {
    let cancelled = false;
    fetchPlans().then((p) => {
      if (!cancelled) {
        setPlans(p);
        setIsLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  return { plans, isLoading };
}
